import type { FilterConfig } from "./types.ts";

/**
 * Smoothing is a latency budget, not a free win.
 *
 * At a 15fps capture rate a causal 3-tap moving average adds ~66ms of group
 * delay on top of ~66ms of frame quantisation, before hysteresis has even had a
 * chance to cross. A blink lasts 100–400ms, so that is a large fraction of the
 * whole event. Which is why the filter is pluggable and `npm run eval` picks the
 * winner from measured F1 and latency rather than the spec picking it up front.
 */
export interface Filter {
  /** @param t frame time in ms. */
  push(value: number, t: number): number;
  reset(): void;
}

class Passthrough implements Filter {
  push(value: number): number {
    return value;
  }
  reset(): void {}
}

class MovingAverage implements Filter {
  #buf: number[] = [];
  constructor(private readonly window: number) {}

  push(value: number): number {
    this.#buf.push(value);
    if (this.#buf.length > this.window) this.#buf.shift();
    let sum = 0;
    for (const v of this.#buf) sum += v;
    return sum / this.#buf.length;
  }

  reset(): void {
    this.#buf = [];
  }
}

/** Rejects single-frame spikes without the smearing a mean causes. */
class MedianFilter implements Filter {
  #buf: number[] = [];
  constructor(private readonly window: number) {}

  push(value: number): number {
    this.#buf.push(value);
    if (this.#buf.length > this.window) this.#buf.shift();
    const sorted = [...this.#buf].sort((a, b) => a - b);
    const mid = sorted.length >> 1;
    return sorted.length % 2
      ? (sorted[mid] as number)
      : ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2;
  }

  reset(): void {
    this.#buf = [];
  }
}

class LowPass {
  #prev: number | null = null;

  filter(value: number, alpha: number): number {
    const out = this.#prev === null ? value : alpha * value + (1 - alpha) * this.#prev;
    this.#prev = out;
    return out;
  }

  get previous(): number | null {
    return this.#prev;
  }

  reset(): void {
    this.#prev = null;
  }
}

/**
 * One-Euro filter (Casiez, Roussel & Vogel, 2012).
 *
 * Built for exactly this tradeoff: heavy smoothing while the signal is at rest,
 * and almost none while it is moving fast. A blink is the fast case, so the
 * closure edge stays sharp and arrives early, while the open-eye baseline stays
 * quiet enough not to trip the hysteresis.
 */
class OneEuro implements Filter {
  readonly #x = new LowPass();
  readonly #dx = new LowPass();
  #lastT: number | null = null;

  constructor(
    private readonly minCutoff: number,
    private readonly beta: number,
    private readonly dCutoff = 1,
  ) {}

  #alpha(cutoff: number, dt: number): number {
    const tau = 1 / (2 * Math.PI * cutoff);
    return 1 / (1 + tau / dt);
  }

  push(value: number, t: number): number {
    const dt = this.#lastT === null ? 1 / 30 : Math.max((t - this.#lastT) / 1000, 1e-4);
    this.#lastT = t;

    const prev = this.#x.previous;
    const derivative = prev === null ? 0 : (value - prev) / dt;
    const edx = this.#dx.filter(derivative, this.#alpha(this.dCutoff, dt));

    const cutoff = this.minCutoff + this.beta * Math.abs(edx);
    return this.#x.filter(value, this.#alpha(cutoff, dt));
  }

  reset(): void {
    this.#x.reset();
    this.#dx.reset();
    this.#lastT = null;
  }
}

export function createFilter(config: FilterConfig): Filter {
  switch (config.kind) {
    case "none":
      return new Passthrough();
    case "moving-average":
      return new MovingAverage(Math.max(1, config.window));
    case "median":
      return new MedianFilter(Math.max(1, config.window));
    case "one-euro":
      return new OneEuro(config.minCutoff, config.beta);
  }
}

/**
 * Quantile over a sliding window, used for the adaptive open-eye baseline.
 *
 * Recomputes by sorting a copy. With a few hundred samples that is far cheaper
 * than it looks and runs once per frame at 15fps; a heap-based structure would
 * be faster and considerably harder to be sure was correct.
 */
export class RollingQuantile {
  #buf: number[];
  #next = 0;
  #count = 0;

  constructor(private readonly capacity: number) {
    this.#buf = new Array<number>(capacity).fill(0);
  }

  push(value: number): void {
    if (!Number.isFinite(value)) return;
    this.#buf[this.#next] = value;
    this.#next = (this.#next + 1) % this.capacity;
    this.#count = Math.min(this.#count + 1, this.capacity);
  }

  get size(): number {
    return this.#count;
  }

  /** @param q 0..1 */
  quantile(q: number): number {
    if (this.#count === 0) return Number.NaN;
    const sorted = this.#buf.slice(0, this.#count).sort((a, b) => a - b);
    const idx = Math.min(this.#count - 1, Math.max(0, Math.round(q * (this.#count - 1))));
    return sorted[idx] as number;
  }

  reset(): void {
    this.#buf.fill(0);
    this.#next = 0;
    this.#count = 0;
  }
}
